import React, { useState } from "react";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { 
  List, 
  Card, 
  Space, 
  Typography, 
  Table, 
  Tag, 
  Button, 
  Popconfirm, 
  Alert, 
  Empty, 
  Spin,
  Checkbox
} from "@pankod/refine-antd";
import { 
  DeleteOutlined, 
  SearchOutlined, 
  HistoryOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import { findDraftDuplicates, getDraftScanStatus, cleanupDuplicateDrafts, getCleanupStatus } from "Services/productServices";
import toast from "react-hot-toast";

const { Text, Paragraph } = Typography;

export const DraftDuplicates: React.FC<IResourceComponentsProps> = () => {
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [cleanupTaskId, setCleanupTaskId] = useState<string | null>(null);
  const [scanProgress, setScanProgress] = useState(0);
  const [cleanupProgress, setCleanupProgress] = useState({ current: 0, total: 0 });

  const pollInterval = React.useRef<any>(null);
  const cleanupPollInterval = React.useRef<any>(null);

  React.useEffect(() => {
    if (taskId) {
      pollInterval.current = setInterval(async () => {
        try {
          const statusRes = await getDraftScanStatus(taskId);
          if (statusRes.data.success) {
            const { status, progress, results, error, foundCount } = statusRes.data;
            setScanProgress(progress);
            
            if (status === 'completed') {
              clearInterval(pollInterval.current);
              setDuplicates(results || []);
              setTaskId(null);
              setLoading(false);
              toast.success(`Scan complete! Found ${foundCount} SKUs with duplicate drafts`);
            } else if (status === 'failed') {
              clearInterval(pollInterval.current);
              setTaskId(null);
              setLoading(false);
              toast.error(error || "Background scan failed");
            }
          }
        } catch (err) {
          console.error("Polling error:", err);
        }
      }, 2000);
    }
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [taskId]);

  React.useEffect(() => {
    if (cleanupTaskId) {
      cleanupPollInterval.current = setInterval(async () => {
        try {
          const statusRes = await getCleanupStatus(cleanupTaskId);
          if (statusRes.data.success) {
            const { status, cleanedCount, totalToClean, error } = statusRes.data;
            setCleanupProgress({ current: cleanedCount, total: totalToClean });
            
            if (status === 'completed') {
              clearInterval(cleanupPollInterval.current);
              setCleanupTaskId(null);
              setCleaning(false);
              toast.success(`Cleanup complete! Purged ${cleanedCount} redundant drafts.`);
              handleScan(); // Refresh
            } else if (status === 'failed') {
              clearInterval(cleanupPollInterval.current);
              setCleanupTaskId(null);
              setCleaning(false);
              toast.error(error || "Cleanup task failed");
            }
          }
        } catch (err) {
          console.error("Cleanup polling error:", err);
        }
      }, 2000);
    }
    return () => {
      if (cleanupPollInterval.current) clearInterval(cleanupPollInterval.current);
    };
  }, [cleanupTaskId]);

  const handleScan = async () => {
    setLoading(true);
    setDuplicates([]);
    setSelectedIds([]);
    setSummary(null);
    setScanProgress(0);
    try {
      const response = await findDraftDuplicates();
      if (response.data.success) {
        setTaskId(response.data.taskId);
        toast.success("Scan task started in background...");
      } else {
        toast.error("Failed to start scan task");
        setLoading(false);
      }
    } catch (error) {
      console.error("Draft scan trigger failed:", error);
      toast.error("Error connecting to bridge");
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (selectedIds.length === 0) {
      toast.error("No drafts selected for deletion");
      return;
    }

    setCleaning(true);
    setCleanupProgress({ current: 0, total: selectedIds.length });
    try {
      const response = await cleanupDuplicateDrafts(selectedIds);
      if (response.data.success) {
        setCleanupTaskId(response.data.taskId);
        toast.success("Cleanup task started in background...");
      } else {
        toast.error("Draft cleanup trigger failed");
        setCleaning(false);
      }
    } catch (error) {
      console.error("Draft cleanup trigger failed:", error);
      toast.error("Error connecting to bridge");
      setCleaning(false);
    }
  };

  const selectOlderVersions = () => {
    const idsToSelect: string[] = [];
    duplicates.forEach(group => {
      // records are sorted newest first in backend
      // keep the first one [0], select all others
      if (group.records.length > 1) {
        group.records.slice(1).forEach((record: any) => {
          idsToSelect.push(record.id);
        });
      }
    });
    setSelectedIds(idsToSelect);
    toast.success(`Selected ${idsToSelect.length} older draft versions`);
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const expandedRowRender = (record: any) => {
    const columns = [
      {
        title: "Keep/Delete",
        key: "select",
        render: (_: any, item: any, index: number) => (
          <Space>
            <Checkbox 
              checked={selectedIds.includes(item.id)}
              onChange={() => toggleSelection(item.id)}
            />
            {index === 0 ? (
              <Tag color="success"><CheckCircleOutlined /> Keep (Newest)</Tag>
            ) : (
              <Tag color="orange"><ExclamationCircleOutlined /> Duplicate Found</Tag>
            )}
          </Space>
        ),
        width: 180
      },
      { title: "ID", dataIndex: "id", key: "id", width: 80 },
      {
        title: "Price",
        dataIndex: "price",
        key: "price",
        render: (val: number) => `$${Number(val).toFixed(2)}`,
        width: 100
      },
      {
        title: "Stock",
        dataIndex: "stock",
        key: "stock",
        render: (val: number) => <Text strong>{val}</Text>,
        width: 80
      },
      {
        title: "Last Updated",
        dataIndex: "updated_at",
        key: "updated_at",
        render: (val: string) => new Date(val).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }),
        width: 200
      }
    ];

    return (
      <Table 
        columns={columns} 
        dataSource={record.records} 
        pagination={false} 
        rowKey="id"
        size="small"
      />
    );
  };

  const mainColumns = [
    {
      title: "Master SKU",
      dataIndex: "masterSku",
      key: "masterSku",
      render: (sku: string) => <Text strong>{sku}</Text>
    },
    {
      title: "Product Name",
      key: "name",
      render: (_: any, record: any) => record.records[0]?.name || "N/A"
    },
    {
      title: "Draft Copies",
      key: "count",
      render: (_: any, record: any) => (
        <Tag color="red">{record.records.length} Drafts</Tag>
      )
    },
    {
      title: "Latest Version Update",
      key: "latest",
      render: (_: any, record: any) => (
        <Space>
          <HistoryOutlined />
          {new Date(record.records[0]?.updated_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
        </Space>
      )
    }
  ];

  return (
    <List 
      title="Unpublished Duplicate Management (Drafts)"
      headerProps={{
        extra: (
          <Space>
            <Button 
              icon={<SearchOutlined />} 
              onClick={handleScan}
              loading={loading}
              type="primary"
            >
              Scan for Draft Duplicates
            </Button>
            {duplicates.length > 0 && (
              <>
                <Button icon={<HistoryOutlined />} onClick={selectOlderVersions}>
                  Auto-Select Older Versions
                </Button>
                <Popconfirm
                  title={`Are you sure you want to delete ${selectedIds.length} redundant drafts?`}
                  onConfirm={handleCleanup}
                  okText="Yes, Delete"
                  cancelText="No"
                  okButtonProps={{ danger: true, loading: cleaning }}
                >
                  <Button 
                    danger 
                    icon={<DeleteOutlined />} 
                    disabled={selectedIds.length === 0}
                  >
                    Delete Selected ({selectedIds.length})
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        )
      }}
    >
      <Alert
        message="Cleaning Up Unpublished Legacy Data"
        description={
          <Paragraph>
            This tool specifically targets products that have multiple "Draft" versions in Strapi. This often happens after failed 
            syncs where the system created a new draft without updating the existing one. Safely purge these redundant records 
            while keeping the most recently updated draft.
          </Paragraph>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {summary && summary.totalDraftDuplicates > 0 && (
        <Alert
          message="System Load Recommendation"
          description={`Identified ${summary.totalDraftDuplicates} redundant draft records. Clearing these will improve CMS performance and sync speed.`}
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip={`Scanning Strapi for redundant drafts... (${scanProgress.toLocaleString()} products checked)`} />
          </div>
        ) : duplicates.length > 0 ? (
          <Table 
            dataSource={duplicates} 
            columns={mainColumns} 
            expandable={{
              expandedRowRender,
              defaultExpandAllRows: true
            }}
            rowKey="masterSku"
          />
        ) : (
          <Empty 
            description="No draft duplicates found in Strapi." 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={handleScan} icon={<SearchOutlined />}>
              Start Scan
            </Button>
          </Empty>
        )}
      </Card>
    </List>
  );
};
